import {FC, useLayoutEffect, useRef, useState} from 'react'
import {useUserProfileContext} from '../../../../context/UserProfileContext'

const ExpandableDescription: FC<{text?: string}> = ({text}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncatable, setIsTruncatable] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  useLayoutEffect(() => {
    const el = textRef.current
    if (!el || isExpanded) return
    const checkOverflow = () => setIsTruncatable(el.scrollHeight > el.clientHeight)
    checkOverflow()
    const observer = new ResizeObserver(checkOverflow)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, isExpanded])

  return (
    <>
      <p ref={textRef} className={isExpanded ? '' : 'truncated-3-line'}>{text || ''}</p>
      {isTruncatable && (
        <div className='readmore-300 w-100 fs-9' onClick={() => setIsExpanded((prev) => !prev)}>
          {isExpanded ? 'Read less' : 'Read more'}
        </div>
      )}
    </>
  )
}

const ProfessionalOverviewTab: FC = () => {
  const {data} = useUserProfileContext()
  const [expandedWorkPositions, setExpandedWorkPositions] = useState({})
  const [expandedOnBoard, setExpandedOnBoard] = useState({})
  const [expandedNonProfit, setExpandedNonProfit] = useState({})
  const [expandedEducation, setExpandedEducation] = useState({})

  const toggleExpandWorkPositions = (index) => {
    setExpandedWorkPositions(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const toggleExpandOnBoard = (index) => {
    setExpandedOnBoard(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const toggleExpandNonProfit = (index) => {
    setExpandedNonProfit(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const toggleExpandEducation = (index) => {
    setExpandedEducation(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  return (
    <div className='professional-overview-content'>
      {/* Primera Columna */}
      <div className='professional-overview-column'>
        {/* Organizations I am involve in / Positions */}
        <div className='professional-overview-section'>
          <div className='professional-overview-section__header'>
            <div className='professional-overview-section__title'>
              <h3>Organizations & Roles</h3>
            </div>
          </div>
          <div className='professional-overview-section__content'>
            {data?.professionalProfile &&
              data.professionalProfile.workPositions.map((item,index) => {
                const isExpanded = expandedWorkPositions[index] || false
                return (
                  <div key={`professional-items-${index}`} className='professional-overview-section__item'>
                    <h4>
                      {item.company} - {item.position}
                    </h4>
                    <p>{`${item.city ? item.city+', ' : ''} ${item.fromYear || ''} - ${item.toYear || (item.currentRole ? 'Present' : '')}`}</p>
                    <ExpandableDescription text={item.description} />
                  </div>
                )
              })}
          </div>
        </div>

        <div className='professional-overview-separator' />

        {/* I am on board */}
        <div className='professional-overview-section'>
          <div className='professional-overview-section__header'>
            <div className='professional-overview-section__title'>
              <h3>I am on board</h3>
            </div>
          </div>

          <div className='professional-overview-section__content'>
            {data?.professionalProfile &&
              data.professionalProfile.onBoard.map((item, index) => {
                const isExpanded = expandedOnBoard[index] || false
                return (
                  <div key={`section-onboard-${index}`} className='professional-overview-section__item'>
                    <h4>
                      {item.company} - {item.position}
                    </h4>
                    <p>{`${item.city ? item.city+', ' : ''} ${item.fromYear || ''} - ${item.toYear || (item.currentRole ? 'Present' : '')}`}</p>
                    <ExpandableDescription text={item.description} />
                  </div>
                )
              })}
          </div>
        </div>

        <div className='professional-overview-separator' />

        {/* Non Profit Involvement */}
        <div className='professional-overview-section'>
          <div className='professional-overview-section__header'>
            <div className='professional-overview-section__title'>
              <h3>Non Profit Involvement</h3>
            </div>
          </div>
          <div className='professional-overview-section__content'>
            {data?.professionalProfile &&
              data.professionalProfile.nonProfitInvolvement.map((item, index) => {
                const isExpanded = expandedNonProfit[index] || false
                return (
                  <div key={`professional-overview-section-${index}`} className='professional-overview-section__item'>
                    <h4>
                      {item.company} - {item.position}
                    </h4>
                    <p>{`${item.city ? item.city+', ' : ''} ${item.fromYear || ''} - ${item.toYear || (item.currentRole ? 'Present' : '')}`}</p>
                    <ExpandableDescription text={item.description} />
                  </div>
                )
              })}
          </div>
        </div>
      </div>

      {/* Segunda Columna */}
      <div className='professional-overview-column'>
        <div className='professional-overview-wrapper'>
          {/* Education */}
          <div className='professional-overview-section'>
            <div className='professional-overview-section__header'>
              <div className='professional-overview-section__title'>
                <h3>Education</h3>
              </div>
            </div>
            <div className='professional-overview-section__content-education'>
              {data?.professionalProfile &&
                data.professionalProfile.education.map((item, index) => {
                  const isExpanded = expandedEducation[index] || false
                  return (
                    <div key={`professional-overview-section-2-${index}`}  className='professional-overview-section__item'>
                      <h4>
                        {item.universityName} - {item.carreer}
                      </h4>
                      <p>{`${item.city ? item.city+', ' : ''} ${item.fromYear || ''} - ${item.toYear || (item.currentRole ? 'Present' : '')}`}</p>
                      <ExpandableDescription text={item.description} />
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
        <div className='professional-overview-separator' />

        {/* Industries */}
        <div className='professional-overview-section'>
          <div className='professional-overview-section__header'>
            <div className='professional-overview-section__title'>
              <h3>Industries</h3>
            </div>
          </div>
          <div className='professional-overview-section__tags'>
            {data?.professionalProfile &&
              data.professionalProfile.industries.map((item,index) => {
                return (
                  <div key={`profesional-overview-industries-${index}`} className='professional-overview-section__tag'>
                    <span>{item}</span>
                  </div>
                )
              })}
          </div>
        </div>

        <div className='professional-overview-separator' />

        {/* Professional Interests */}
        <div className='professional-overview-section'>
          <div className='professional-overview-section__header'>
            <div className='professional-overview-section__title'>
              <h3>Professional Interests</h3>
            </div>
          </div>
          <div className='professional-overview-section__tags'>
            {data?.professionalProfile &&
              data.professionalProfile.professionalInterest.map((item, index) => {
                return (
                  <div key={`professional-interest-section-${index}`}  className='professional-overview-section__tag'>
                    <span>{item}</span>
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </div>
  )
}

export {ProfessionalOverviewTab}
